import React, { useEffect, useMemo, useState } from 'react';
import { CircularProgress } from '@mui/material';
import { XMarkIcon } from '@heroicons/react/24/outline';
import {
  Case,
  CaseClosureStatus,
  CaseFinalEvaluation,
  CASE_FINAL_EVALUATIONS,
} from '../../types';
import { t } from '../../utils/translations';

export interface CaseClosureFormValues {
  status: CaseClosureStatus;
  sessionNumber: number | null;
  problemUnderstanding: string;
  observedChanges: string;
  gospelRelationship: string;
  finalEvaluation: CaseFinalEvaluation | null;
  evaluationDetails: string;
  followUpRequired: boolean;
  unfinishedReason: string;
  nextSteps: string;
  cancellationReason: string;
}

interface CaseClosureDialogProps {
  open: boolean;
  caseItem: Case | null;
  reportCount: number;
  loading: boolean;
  onClose: () => void;
  onSubmit: (values: CaseClosureFormValues) => void;
  /** In the add-report flow, closure belongs to the next session (never session 0). */
  useNextSession?: boolean;
}

const emptyValues = (
  reportCount: number,
  useNextSession: boolean
): CaseClosureFormValues => ({
  status: 'finished',
  sessionNumber:
    useNextSession && reportCount > 0
      ? reportCount
      : reportCount > 0
        ? reportCount - 1
        : null,
  problemUnderstanding: '',
  observedChanges: '',
  gospelRelationship: '',
  finalEvaluation: null,
  evaluationDetails: '',
  followUpRequired: false,
  unfinishedReason: '',
  nextSteps: '',
  cancellationReason: '',
});

const inputClass =
  'mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 disabled:opacity-50';

const CaseClosureDialog: React.FC<CaseClosureDialogProps> = ({
  open,
  caseItem,
  reportCount,
  loading,
  onClose,
  onSubmit,
  useNextSession = false,
}) => {
  const [values, setValues] = useState<CaseClosureFormValues>(() =>
    emptyValues(reportCount, useNextSession)
  );
  const [error, setError] = useState('');
  const sessionOptions = useMemo(
    () =>
      useNextSession
        ? Array.from({ length: Math.max(0, reportCount) }, (_, index) => index + 1)
        : Array.from({ length: Math.max(0, reportCount) }, (_, index) => index),
    [reportCount, useNextSession]
  );

  useEffect(() => {
    if (!open) return;
    setValues(emptyValues(reportCount, useNextSession));
    setError('');
  }, [open, reportCount, caseItem?.id, useNextSession]);

  if (!open || !caseItem) return null;

  const update = <K extends keyof CaseClosureFormValues>(
    key: K,
    value: CaseClosureFormValues[K]
  ) => {
    setValues((current) => ({ ...current, [key]: value }));
    setError('');
  };

  const selectEvaluation = (evaluation: CaseFinalEvaluation) => {
    setValues((current) => ({
      ...current,
      finalEvaluation: evaluation,
      followUpRequired: evaluation === 'needs_follow_up',
      evaluationDetails:
        evaluation === 'continue_alone' ? '' : current.evaluationDetails,
    }));
    setError('');
  };

  const detailsHint =
    values.finalEvaluation && values.finalEvaluation !== 'continue_alone'
      ? t.caseClosure.evaluationDetailsHint[values.finalEvaluation]
      : '';

  const validate = (): string | null => {
    if (values.status === 'unfinished') {
      if (!values.unfinishedReason.trim() || !values.nextSteps.trim()) {
        return t.caseClosure.unfinishedRequired;
      }
      return null;
    }
    if (values.status === 'cancelled') {
      if (!values.cancellationReason.trim()) {
        return t.caseClosure.cancellationRequired;
      }
      return null;
    }
    if (
      !values.problemUnderstanding.trim() ||
      !values.observedChanges.trim() ||
      !values.gospelRelationship.trim()
    ) {
      return t.caseClosure.requiredFields;
    }
    if (!values.finalEvaluation) {
      return t.caseClosure.evaluationRequired;
    }
    if (
      values.finalEvaluation === 'needs_follow_up' &&
      !values.evaluationDetails.trim()
    ) {
      return t.caseClosure.followUpRequired;
    }
    return null;
  };

  const handleSubmit = () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    if (values.status === 'unfinished') {
      onSubmit({
        ...values,
        problemUnderstanding: '',
        observedChanges: '',
        gospelRelationship: '',
        finalEvaluation: null,
        evaluationDetails: '',
        followUpRequired: false,
        unfinishedReason: values.unfinishedReason.trim(),
        nextSteps: values.nextSteps.trim(),
        cancellationReason: '',
      });
      return;
    }

    if (values.status === 'cancelled') {
      onSubmit({
        ...values,
        problemUnderstanding: '',
        observedChanges: '',
        gospelRelationship: '',
        finalEvaluation: null,
        evaluationDetails: '',
        followUpRequired: false,
        unfinishedReason: '',
        nextSteps: '',
        cancellationReason: values.cancellationReason.trim(),
      });
      return;
    }

    onSubmit({
      ...values,
      problemUnderstanding: values.problemUnderstanding.trim(),
      observedChanges: values.observedChanges.trim(),
      gospelRelationship: values.gospelRelationship.trim(),
      evaluationDetails:
        values.finalEvaluation === 'continue_alone'
          ? ''
          : values.evaluationDetails.trim(),
      followUpRequired: values.finalEvaluation === 'needs_follow_up',
      unfinishedReason: '',
      nextSteps: '',
      cancellationReason: '',
    });
  };

  return (
    <div
      className="fixed inset-0 z-[1400] flex items-stretch justify-center sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="case-closure-title"
    >
      <div
        className="absolute inset-0 bg-slate-900/40"
        aria-hidden="true"
        onClick={() => {
          if (!loading) onClose();
        }}
      />

      <div className="safe-pt safe-pb relative flex h-full w-full flex-col overflow-hidden bg-white shadow-lg sm:h-[min(90vh,46rem)] sm:max-w-2xl sm:rounded-xl sm:border sm:border-slate-200">
        <header className="flex shrink-0 items-start justify-between gap-3 border-b border-slate-200 px-4 py-4 sm:px-5">
          <div className="min-w-0">
            <h2 id="case-closure-title" className="text-base font-semibold text-slate-900">
              {t.caseClosure.title}
            </h2>
            <p className="mt-0.5 truncate text-sm text-slate-500">
              {caseItem.counseledName} · {caseItem.title}
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              if (!loading) onClose();
            }}
            aria-label={t.common.close}
            className="shrink-0 rounded-md p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </header>

        <div className="flex-1 space-y-6 overflow-y-auto px-4 py-5 sm:px-5">
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {t.caseClosure.sectionOutcome}
            </h3>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <label className="text-sm font-medium text-slate-800">
                {t.caseClosure.status} *
                <select
                  value={values.status}
                  disabled={loading}
                  onChange={(event) =>
                    update('status', event.target.value as CaseClosureStatus)
                  }
                  className={inputClass}
                >
                  <option value="finished">{t.caseClosure.outcome.finished}</option>
                  <option value="unfinished">{t.caseClosure.outcome.unfinished}</option>
                  <option value="cancelled">{t.caseClosure.outcome.cancelled}</option>
                </select>
              </label>

              <label className="text-sm font-medium text-slate-800">
                {t.caseClosure.lastSession}
                <select
                  value={values.sessionNumber ?? ''}
                  disabled={loading}
                  onChange={(event) =>
                    update(
                      'sessionNumber',
                      event.target.value === '' ? null : Number(event.target.value)
                    )
                  }
                  className={inputClass}
                >
                  <option value="">{t.caseClosure.noSession}</option>
                  {sessionOptions.map((session) => (
                    <option key={session} value={session}>
                      {t.sessionReports.sessionNumber} {session}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </section>

          {values.status === 'finished' && (
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                {t.caseClosure.sectionEvaluation}
              </h3>
            <div className="mt-3 space-y-3">
              <label className="block text-sm font-medium text-slate-800">
                {t.caseClosure.problemUnderstanding} *
                <textarea
                  rows={3}
                  value={values.problemUnderstanding}
                  disabled={loading}
                  onChange={(event) =>
                    update('problemUnderstanding', event.target.value)
                  }
                  className={`${inputClass} resize-none`}
                />
              </label>

              <label className="block text-sm font-medium text-slate-800">
                {t.caseClosure.observedChanges} *
                <textarea
                  rows={3}
                  value={values.observedChanges}
                  disabled={loading}
                  onChange={(event) => update('observedChanges', event.target.value)}
                  className={`${inputClass} resize-none`}
                />
              </label>

              <label className="block text-sm font-medium text-slate-800">
                {t.caseClosure.gospelRelationship} *
                <textarea
                  rows={3}
                  value={values.gospelRelationship}
                  disabled={loading}
                  onChange={(event) =>
                    update('gospelRelationship', event.target.value)
                  }
                  className={`${inputClass} resize-none`}
                />
              </label>

              <div>
                <p className="text-sm font-medium text-slate-800">
                  {t.caseClosure.finalEvaluation} *
                </p>
                <div className="mt-2 grid gap-2 sm:grid-cols-2">
                  {CASE_FINAL_EVALUATIONS.map((evaluation) => {
                    const active = values.finalEvaluation === evaluation;
                    return (
                      <button
                        key={evaluation}
                        type="button"
                        disabled={loading}
                        onClick={() => selectEvaluation(evaluation)}
                        className={`rounded-lg border px-3 py-2.5 text-left text-sm font-medium transition disabled:opacity-50 ${
                          active
                            ? 'border-brand-600 bg-brand-50 text-brand-800'
                            : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        {t.caseClosure.evaluation[evaluation]}
                      </button>
                    );
                  })}
                </div>
              </div>

              {values.finalEvaluation && values.finalEvaluation !== 'continue_alone' && (
                <label className="block text-sm font-medium text-slate-800">
                  {t.caseClosure.evaluationDetails}
                  {values.finalEvaluation === 'needs_follow_up' ? ' *' : ''}
                  <span className="mt-0.5 block text-xs font-normal text-slate-500">
                    {detailsHint}
                  </span>
                  <textarea
                    rows={2}
                    value={values.evaluationDetails}
                    disabled={loading}
                    onChange={(event) =>
                      update('evaluationDetails', event.target.value)
                    }
                    className={`${inputClass} resize-none`}
                  />
                </label>
              )}
            </div>
            </section>
          )}

          {values.status === 'unfinished' && (
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                {t.caseClosure.outcome.unfinished}
              </h3>
              <div className="mt-3 space-y-3">
                <label className="block text-sm font-medium text-slate-800">
                  {t.caseClosure.unfinishedReason} *
                  <textarea
                    rows={3}
                    value={values.unfinishedReason}
                    disabled={loading}
                    onChange={(event) =>
                      update('unfinishedReason', event.target.value)
                    }
                    className={`${inputClass} resize-none`}
                  />
                </label>
                <label className="block text-sm font-medium text-slate-800">
                  {t.caseClosure.nextSteps} *
                  <textarea
                    rows={3}
                    value={values.nextSteps}
                    disabled={loading}
                    onChange={(event) => update('nextSteps', event.target.value)}
                    className={`${inputClass} resize-none`}
                  />
                </label>
              </div>
            </section>
          )}

          {values.status === 'cancelled' && (
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                {t.caseClosure.outcome.cancelled}
              </h3>
              <div className="mt-3">
                <label className="block text-sm font-medium text-slate-800">
                  {t.caseClosure.cancellationReason} *
                  <textarea
                    rows={4}
                    value={values.cancellationReason}
                    disabled={loading}
                    onChange={(event) =>
                      update('cancellationReason', event.target.value)
                    }
                    className={`${inputClass} resize-none`}
                  />
                </label>
              </div>
            </section>
          )}

          <p aria-live="polite" className="min-h-[1.25rem] text-sm text-red-600">
            {error}
          </p>
        </div>

        <footer className="flex shrink-0 items-center justify-end gap-2 border-t border-slate-200 px-4 py-3 sm:px-5">
          <button
            type="button"
            disabled={loading}
            onClick={onClose}
            className="rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            {t.common.cancel}
          </button>
          <button
            type="button"
            disabled={loading}
            onClick={handleSubmit}
            className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {loading && <CircularProgress size={14} color="inherit" />}
            {t.caseClosure.closeCase}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default CaseClosureDialog;
